import { createApiClient, apiClient } from "../../../api/apiClient";
import { AppDispatch } from "../../../store";
import { setQuestions } from "../../../store/slices/leadershipWalkRedirectSlice";
import {
  handleLeadershipWalkErrorWithFriendlyMessage,
  LEADERSHIP_WALK_API_NAMES,
} from "../../../store/utils/leadershipWalkErrorHandler";

/**
 * Fetches leadership walk details based on the walk ID
 * @param walkId - ID of the leadership walk
 * @param dispatch - Redux dispatch function
 * @param setError - Function to set error message
 * @param setLoading - Function to toggle loading state
 */
export const fetchLeadershipWalkQuestions = async (
  walkId: number,
  dispatch: AppDispatch,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void
) => {
  if (!walkId) {
    setError("Invalid Walk ID");
    setLoading(false);
    return;
  }

  setError(null);
  setLoading(true);

  try {
    const api = createApiClient();
    const response = await apiClient(api, `/leadership-walks/${walkId}`, "GET");
    console.log("API response:", response);
    console.log("api response data:", response.data);
    
    if (response && response.data) {
      console.log("Leadership walk details received:", response.data);

      // Check if the response data is an object with expected category keys
      const data = response.data;

      // Ensure the data has the expected structure or transform it
      if (typeof data === "object" && !Array.isArray(data)) {
        // Create a standardized structure
        const organizedData = {
          Safety: data.Safety || [],
          People: data.People || [],
          Accuracy: data.Accuracy || [],
          Costs: data.Costs || [],
          Reliability: data.Reliability || [],
        };

        // Update Redux state with organized data
        dispatch(setQuestions(organizedData));
        setError(null);
      } else if (Array.isArray(data)) {
        // If it's an array, organize it by categories
        const organizedData = {
          Safety: data.filter((q) => q.SPARC_Category === "Safety"),
          People: data.filter((q) => q.SPARC_Category === "People"),
          Accuracy: data.filter((q) => q.SPARC_Category === "Accuracy"),
          Costs: data.filter(
            (q) => q.SPARC_Category === "Costs" || q.SPARC_Category === "Cost"
          ),
          Reliability: data.filter((q) => q.SPARC_Category === "Reliability"),
        };

        dispatch(setQuestions(organizedData));
        setError(null);
      } else {
        const unexpectedFormatError = new Error("Unexpected response format from server");
        
        handleLeadershipWalkErrorWithFriendlyMessage(
          LEADERSHIP_WALK_API_NAMES.FETCH_WALK_DETAILS,
          unexpectedFormatError,
          true,
          { walkId, reason: "Unexpected response format" }
        );
        
        setError("Unexpected response format from server");
      }
    } else {
      const noDataError = new Error("Unable to fetch leadership walk details");
      
      handleLeadershipWalkErrorWithFriendlyMessage(
        LEADERSHIP_WALK_API_NAMES.FETCH_WALK_DETAILS,
        noDataError,
        true,
        { walkId, reason: "Empty or invalid response data" }
      );
      
      setError("Unable to fetch leadership walk details");
    }
  } catch (e) {
    console.error("Error fetching leadership walk details:", e);
    
    // Use centralized error handler with modal overlay
    handleLeadershipWalkErrorWithFriendlyMessage(
      LEADERSHIP_WALK_API_NAMES.FETCH_WALK_DETAILS,
      e,
      true,
      { walkId }
    );
    
    setError("Unable to fetch leadership walk details");
  } finally {
    setLoading(false);
  }
};
